export const FINANCING_PROJECTION_MAX_MONTHS = 360;
export const FINANCING_FUTURE_PROJECTION_MONTHS = 60;
export const FINANCING_GAP_COLOR = "#f0883e";

export function monthlyRateFromAnnualPct(annualPct: number): number {
  if (annualPct <= -100) {
    return 0;
  }

  return Math.pow(1 + annualPct / 100, 1 / 12) - 1;
}

export interface FinancingTimelinePoint {
  month: number;
  projectedRent: number;
  installment: number;
}

export interface FinancingProjectionResult {
  timeline: FinancingTimelinePoint[];
  milestoneMonth: number | null;
}

function shouldSampleMonth(month: number, maxMonth: number): boolean {
  if (month === 0 || month === maxMonth) {
    return true;
  }

  if (month <= 24) {
    return month % 6 === 0;
  }

  return month % 12 === 0;
}

function buildSampledTimeline(
  startingRent: number,
  installment: number,
  monthlyIndexRate: number,
  maxMonth: number,
  milestoneMonth: number | null
): FinancingTimelinePoint[] {
  const timeline: FinancingTimelinePoint[] = [];
  let projectedRent = roundCurrency(Math.max(0, startingRent));

  for (let month = 0; month <= maxMonth; month += 1) {
    if (month > 0) {
      projectedRent = roundCurrency(projectedRent * (1 + monthlyIndexRate));
    }

    if (shouldSampleMonth(month, maxMonth)) {
      timeline.push({ month, projectedRent, installment });
    }
  }

  if (milestoneMonth !== null && !timeline.some((point) => point.month === milestoneMonth)) {
    let rentAtMilestone = roundCurrency(Math.max(0, startingRent));

    for (let month = 1; month <= milestoneMonth; month += 1) {
      rentAtMilestone = roundCurrency(rentAtMilestone * (1 + monthlyIndexRate));
    }

    timeline.push({ month: milestoneMonth, projectedRent: rentAtMilestone, installment });
    timeline.sort((first, second) => first.month - second.month);
  }

  return timeline;
}

export function buildFinancingProjectionTimeline(
  averageMonthlyNetRent: number,
  monthlyInstallment: number,
  ipca12mAccumulatedPct: number,
  coversInstallment: boolean,
  maxMonths: number = FINANCING_PROJECTION_MAX_MONTHS
): FinancingProjectionResult {
  const installment = roundCurrency(Math.max(0, monthlyInstallment));
  const startingRent = roundCurrency(Math.max(0, averageMonthlyNetRent));

  if (installment <= 0) {
    return {
      timeline: [{ month: 0, projectedRent: startingRent, installment: 0 }],
      milestoneMonth: 0,
    };
  }

  const monthlyIndexRate = monthlyRateFromAnnualPct(ipca12mAccumulatedPct);

  if (coversInstallment || startingRent >= installment) {
    return {
      timeline: buildSampledTimeline(
        startingRent,
        installment,
        monthlyIndexRate,
        FINANCING_FUTURE_PROJECTION_MONTHS,
        0
      ),
      milestoneMonth: 0,
    };
  }

  let projectedRent = startingRent;
  let milestoneMonth: number | null = null;
  let months = 0;

  while (projectedRent < installment && months < maxMonths) {
    months += 1;
    projectedRent = roundCurrency(projectedRent * (1 + monthlyIndexRate));

    if (projectedRent >= installment) {
      milestoneMonth = months;
      break;
    }
  }

  const horizonAfterMilestone = milestoneMonth === null
    ? FINANCING_FUTURE_PROJECTION_MONTHS
    : Math.min(milestoneMonth + FINANCING_FUTURE_PROJECTION_MONTHS, maxMonths);

  return {
    timeline: buildSampledTimeline(
      startingRent,
      installment,
      monthlyIndexRate,
      horizonAfterMilestone,
      milestoneMonth
    ),
    milestoneMonth,
  };
}

export function formatFinancingTimelineLabel(month: number): string {
  if (month === 0) {
    return "Hoje";
  }

  if (month % 12 === 0) {
    const years = month / 12;
    return years === 1 ? "+1 ano" : `+${years} anos`;
  }

  if (month < 24) {
    return `+${month}m`;
  }

  return "";
}

export function getMonthlyGap(projectedRent: number, installment: number): number {
  return roundCurrency(Math.max(0, installment - projectedRent));
}

export function normalizeProjectionTimeline(
  timeline: FinancingTimelinePoint[],
  monthlyInstallment: number,
  preserveVariableInstallments = false
): FinancingTimelinePoint[] {
  const fallbackInstallment = roundCurrency(Math.max(0, monthlyInstallment));

  return timeline.map((point) => {
    if (point.installment > 0 || preserveVariableInstallments) {
      return point;
    }

    return {
      ...point,
      installment: fallbackInstallment,
    };
  });
}

export function injectFinancingMilestonePoint(
  timeline: FinancingTimelinePoint[],
  milestoneMonth: number | null,
  startingRent: number,
  monthlyInstallment: number,
  monthlyIndexRate: number
): FinancingTimelinePoint[] {
  if (milestoneMonth === null || timeline.some((point) => point.month === milestoneMonth)) {
    return timeline;
  }

  const installment = roundCurrency(Math.max(0, monthlyInstallment));
  let rentAtMilestone = roundCurrency(Math.max(0, startingRent));

  for (let month = 1; month <= milestoneMonth; month += 1) {
    rentAtMilestone = roundCurrency(rentAtMilestone * (1 + monthlyIndexRate));
  }

  return [
    ...timeline,
    {
      month: milestoneMonth,
      projectedRent: rentAtMilestone,
      installment,
    },
  ].sort((first, second) => first.month - second.month);
}

export function resolveFinancingMilestoneChartIndex(
  timeline: FinancingTimelinePoint[],
  milestoneMonth: number | null
): number {
  if (milestoneMonth === null || timeline.length === 0) {
    return -1;
  }

  const exactIndex = timeline.findIndex((point) => point.month === milestoneMonth);
  if (exactIndex >= 0) {
    return exactIndex;
  }

  if (milestoneMonth === 0) {
    return 0;
  }

  const crossingIndex = timeline.findIndex(
    (point) => point.installment > 0 && point.projectedRent >= point.installment
  );
  if (crossingIndex >= 0) {
    return crossingIndex;
  }

  return -1;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}
